import { useState, useEffect, useRef, useMemo } from 'react';
import { useQuery } from '@apollo/client';
import MatchUp from './matchup';
import SelectGender from '../setup/selectgender';
import { GenderType } from '../../models/index';
import { matches2026 as mMatches } from '../../utilities/cga';
import { matches2026 as wMatches } from '../../utilities/wcga3';
// import { matches2025 as wMatches } from '../../utilities/wcga2';
import matchupGridStyles from './matchupgrid.module.css';
import GetSiteSettings from '../../apollo/queries/GetSiteSettings.graphql';
import { useUser } from '../../user-provider';

const GLOBAL_SETTINGS_ID = 'homepage';
const getSeasonSettingsId = (seasonNum) => `Season ${seasonNum}`;

function MatchupGrid() {
  const { isAuthenticating } = useUser();
  const [gender, setGender] = useState(GenderType.COED);
  const [displayedMatches, setDisplayedMatches] = useState([]);
  const [hasMore, setHasMore] = useState(false);
  const [page, setPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const loadMoreRef = useRef();
  const {
    genderContainer,
    skeletonRow,
    skeletonTitle,
    skeletonDate,
    skeletonLogos,
    skeletonLogo,
    skeletonButton,
  } = matchupGridStyles;

  // Skeleton component for loading state
  const MatchSkeleton = () => (
    <div className={skeletonRow}>
      <div className={skeletonTitle} />
      <div className={skeletonDate} />
      <div className={skeletonLogos}>
        <div className={skeletonLogo} />
        <div className={skeletonLogo} />
      </div>
      <div className={skeletonButton} />
    </div>
  );

  // First, load global settings to get active season (skip until auth resolves)
  const { data: globalData, loading: loadingGlobal } = useQuery(
    GetSiteSettings,
    {
      variables: { id: GLOBAL_SETTINGS_ID },
      fetchPolicy: 'cache-first',
      skip: isAuthenticating,
    }
  );

  // Check for preview mode via /preview route or URL param
  const isPreviewRoute = window.location.pathname === '/preview';
  const params = new URLSearchParams(window.location.search);
  const hasPreviewParam = params.get('preview') === 'true';
  const seasonParam = params.get('season');
  const isPreviewMode = isPreviewRoute || hasPreviewParam;
  const previewSeason = seasonParam ? parseInt(seasonParam, 10) : null;

  // Get active season from global settings (or use preview season if specified)
  const currentActiveSeason =
    previewSeason || globalData?.getSiteSettings?.currentSeason;

  // Then load the active season's data (only when we know the season and auth is resolved)
  const { data: settingsData, loading: loadingSettings } = useQuery(
    GetSiteSettings,
    {
      variables: { id: getSeasonSettingsId(currentActiveSeason || 6) },
      fetchPolicy: previewSeason ? 'network-only' : 'cache-first',
      skip: isAuthenticating || (!currentActiveSeason && !previewSeason),
    }
  );

  // Parse database matches or fall back to static files
  const { dbMaleMatches, dbFemaleMatches, useDatabase } = useMemo(() => {
    if (settingsData?.getSiteSettings?.homepageData) {
      try {
        // Handle potentially double-stringified data from DynamoDB/GraphQL
        let parsed =
          typeof settingsData.getSiteSettings.homepageData === 'string'
            ? JSON.parse(settingsData.getSiteSettings.homepageData)
            : settingsData.getSiteSettings.homepageData;
        // Check if we got another string (double-stringified case)
        if (typeof parsed === 'string') {
          parsed = JSON.parse(parsed);
        }

        if (parsed?.weeks?.length > 0) {
          // Get week status (handle legacy 'published' field)
          const getStatus = (week) =>
            week.status || (week.published ? 'live' : 'draft');

          // Filter weeks based on mode
          // Preview mode: show 'live' and 'preview' weeks
          // Normal mode: show only 'live' weeks
          const visibleWeeks = parsed.weeks.filter((week) => {
            const status = getStatus(week);
            return isPreviewMode
              ? status === 'live' || status === 'preview'
              : status === 'live';
          });

          if (visibleWeeks.length === 0) {
            // No visible weeks, fall back to static files
            return {
              dbMaleMatches: null,
              dbFemaleMatches: null,
              useDatabase: false,
            };
          }

          // Flatten all matches from visible weeks
          const allDbMatches = visibleWeeks.flatMap(
            (week) => week.matches || []
          );

          // Separate by gender tag
          const maleMatches = allDbMatches.filter((m) =>
            m.tags?.includes('MALE')
          );
          const femaleMatches = allDbMatches.filter(
            (m) => m.tags?.includes('FEMALE') || !m.tags?.includes('MALE')
          );

          return {
            dbMaleMatches: [maleMatches],
            dbFemaleMatches: [femaleMatches],
            useDatabase: true,
          };
        }
      } catch (e) {
        console.error('Error parsing homepage data:', e);
      }
    }
    return { dbMaleMatches: null, dbFemaleMatches: null, useDatabase: false };
  }, [settingsData, isPreviewMode]);

  // Use database matches if available, otherwise fall back to static files
  const mMatchesSource = useDatabase ? dbMaleMatches : mMatches;
  const wMatchesSource = useDatabase ? dbFemaleMatches : wMatches;

  // Intersection Observer for infinite scroll
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !isLoading) {
          setPage((prev) => prev + 1);
        }
      },
      { threshold: 0.1 }
    );

    if (loadMoreRef.current) {
      observer.observe(loadMoreRef.current);
    }

    return () => observer.disconnect();
  }, [hasMore, isLoading]);

  /*
  const [matchList, setMatchList] = useState([]);
  const apolloClient = useApolloClient();

  useEffect(() => {
    const list = [];

    matches.forEach((matchRow) => {
      matchRow.forEach((match) => {
        console.log(match);
        apolloClient
          .query({
            query: GetMatch,
            variables: {
              sessionKey: match.sessionId,
            },
          })
          .then(({ data }) => {
            list.push(data.SessionByKey.items[0]);
          });
      });
    });

    console.log(list);
    setMatchList(list);
  }, [apolloClient]);

  console.log(matchList);
  */

  const splitRowIfNeeded = (row, rowIndex, matches) => {
    while (row.length > 8) {
      // Find natural break points based on time gaps
      const timeGaps = [];
      for (let i = 1; i < row.length; i++) {
        const gap =
          new Date(row[i].gameTime).getTime() -
          new Date(row[i - 1].gameTime).getTime();
        timeGaps.push({ index: i, gap });
      }

      // Sort gaps by size to find best split point
      timeGaps.sort((a, b) => b.gap - a.gap);

      // Split at largest gap, or at 8 items if no significant gaps
      const splitIndex =
        timeGaps[0].gap > 3600000 // 1 hour gap
          ? Math.min(timeGaps[0].index, 8)
          : 8;

      // Create new row with remainder
      const newRow = row.splice(splitIndex);
      matches.splice(rowIndex + 1, 0, newRow);

      // Continue with the next row if it also needs splitting
      row = newRow;
      rowIndex++;
    }
  };

  const selectMatches = (gender, mSource, wSource) => {
    // First get all matches based on gender filter
    let allMatches = [];
    switch (gender) {
      case GenderType.MALE:
        mSource.forEach((row) => allMatches.push(...row));
        break;
      case GenderType.FEMALE:
        wSource.forEach((row) => allMatches.push(...row));
        break;
      case GenderType.COED:
      default:
        wSource.forEach((row) => allMatches.push(...row));
        mSource.forEach((row) => allMatches.push(...row));
        break;
    }

    const now = new Date().getTime();
    const minute = 60 * 1000;
    const hour = 60 * minute;
    const day = 24 * hour;

    // Initialize sections with meaningful names
    const sections = {
      inProgress: [], // Matches happening now (-10min to +2hours from start time)
      justFinished: [], // Matches that just ended (last 3 hours)
      upcoming: [], // Next 24 hours
      recent: [], // Last 24 hours (excluding justFinished)
      future: [], // Beyond next 24 hours
      past: [], // Beyond last 24 hours
    };

    // Sort matches into sections
    allMatches.forEach((match) => {
      const matchTime = new Date(match.gameTime).getTime();
      const timeDiff = matchTime - now;

      if (timeDiff >= -2 * hour && timeDiff <= 2 * hour) {
        sections.inProgress.push(match);
      } else if (timeDiff < -2 * hour && timeDiff >= -3 * hour) {
        sections.justFinished.push(match);
      } else if (timeDiff > hour) {
        const matchDate = new Date(match.gameTime).setHours(0, 0, 0, 0);
        const todayDate = new Date().setHours(0, 0, 0, 0);
        if (matchDate === todayDate) {
          sections.upcoming.push(match);
        } else {
          sections.future.push(match);
        }
      } else if (timeDiff < -3 * hour && timeDiff >= -day) {
        sections.recent.push(match);
      } else if (timeDiff < -day) {
        sections.past.push(match);
      }
    });

    // Sort each section by time appropriately
    sections.inProgress.sort(
      (a, b) => new Date(a.gameTime) - new Date(b.gameTime)
    );
    sections.justFinished.sort(
      (a, b) => new Date(b.gameTime) - new Date(a.gameTime)
    ); // Most recent first
    sections.upcoming.sort(
      (a, b) => new Date(a.gameTime) - new Date(b.gameTime)
    );
    sections.recent.sort((a, b) => new Date(b.gameTime) - new Date(a.gameTime)); // Most recent first
    sections.future.sort((a, b) => new Date(a.gameTime) - new Date(b.gameTime));
    sections.past.sort((a, b) => new Date(b.gameTime) - new Date(a.gameTime)); // Most recent first

    // Create final array with only non-empty sections
    const mergedMatches = [];

    if (sections.inProgress.length > 0) mergedMatches.push(sections.inProgress);
    if (sections.justFinished.length > 0)
      mergedMatches.push(sections.justFinished);
    if (sections.upcoming.length > 0) mergedMatches.push(sections.upcoming);
    if (sections.future.length > 0) mergedMatches.push(sections.future);

    // Handle previous matches - split them into initial row of 8
    const previousMatches = [...sections.recent, ...sections.past];
    if (previousMatches.length > 0) {
      // Split into chunks of 8
      for (let i = 0; i < previousMatches.length; i += 8) {
        const chunk = previousMatches.slice(i, i + 8);
        mergedMatches.push(chunk);
      }
    }

    // Apply additional splitting to any remaining long rows
    mergedMatches.forEach((row, index) => {
      if (row.length > 0) {
        splitRowIfNeeded(row, index, mergedMatches);
      }
    });

    return mergedMatches;
  };

  // Load matches when gender, page, or data source changes
  useEffect(() => {
    // Don't process while still loading settings
    if (loadingSettings) return;

    const allMatches = selectMatches(gender, mMatchesSource, wMatchesSource);
    const MATCHES_PER_PAGE = 3; // Number of rows to load at a time

    setIsLoading(true);

    // Calculate which matches to show based on current page
    const endIndex = page * MATCHES_PER_PAGE;
    const newMatches = allMatches.slice(0, endIndex);

    setDisplayedMatches(newMatches);
    setHasMore(endIndex < allMatches.length);
    setIsLoading(false);
  }, [gender, page, mMatchesSource, wMatchesSource, loadingSettings]);

  return (
    <div className="heroGrid">
      <div className={genderContainer}>
        <SelectGender gender={gender} setGender={setGender} />
      </div>
      {loadingSettings || loadingGlobal ? (
        <div>
          <MatchSkeleton />
          <MatchSkeleton />
          <MatchSkeleton />
        </div>
      ) : (
        <ul>
          {displayedMatches &&
            displayedMatches.map((matchRow, i) => {
              let headText = '';

              if (matchRow.length === 0) {
                return null;
              }

              // Determine heading based on match times
              const firstMatchTime = new Date(matchRow[0].gameTime).getTime();
              const now = new Date().getTime();
              const timeDiff = firstMatchTime - now;
              const hour = 60 * 60 * 1000;

              if (timeDiff >= -2 * hour && timeDiff <= 2 * hour) {
                headText = 'In Progress';
              } else if (timeDiff < -2 * hour && timeDiff >= -3 * hour) {
                headText = 'Just Finished';
              } else if (timeDiff > hour) {
                const matchDate = new Date(firstMatchTime).setHours(0, 0, 0, 0);
                const todayDate = new Date().setHours(0, 0, 0, 0);
                headText =
                  matchDate === todayDate ? 'Coming Up' : 'Future Matches';
              } else {
                headText = 'Previous Matches';
              }

              return headText ? (
                <li key={i}>
                  <h5>{headText}</h5>
                  {matchRow.map((match, j) => (
                    <MatchUp match={match} key={j} />
                  ))}
                </li>
              ) : null;
            })}
        </ul>
      )}
      {hasMore && (
        <div
          ref={loadMoreRef}
          style={{ height: '20px', margin: '20px 0', textAlign: 'center' }}
        >
          {isLoading ? 'Loading more matches...' : 'Scroll for more matches'}
        </div>
      )}
    </div>
  );
}

export default MatchupGrid;
